import {useSharedFilter} from '../../../shared/navigation/useSharedReport';
import { TableSearchScope, SectionTableSearch } from "../../../shared/ui/table/TableSearchScope";
// src/features/boe/components/StaffSnapshotByYear.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { getDoc, doc } from "firebase/firestore";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

import { db } from "../../../firebase";
import { CommonTable } from "../../../shared/ui/table/CommonTable.jsx";
import { HeaderToggleButton } from "../../../shared/ui/overlay/HeaderToggleButton.jsx";
import { HoverPopover } from "../../../shared/ui/overlay/HoverPopover.jsx";
import { LineChartWithToggles } from "../../../shared/ui/chart/LineChartWithToggles.jsx";
import { formatFiscalYearFromStart } from "../../../shared/utils/fiscalYearUtils.js";
import { BOE_PROGRAM_DOC_IDS } from "../../../shared/constants/programBudget.js";

import {
  POSITION_ORDER,
  attachIncreaseFields,
  avgSalaryPerFteAcrossPrograms,
  buildStaffChartForSection,
  buildTwoLineHeader,
  buildYearAvailability,
  collectRoleKeysForYears,
  formatFte,
  getStaffPositionGroup,
  getYearsForSection,
  slugifyKey,
  sumRoleAcrossPrograms,
  sumSalaryAcrossPrograms,
  toYearNumber,
} from "../utils/staffSnapshotUtils.js";

/**
 * Firestore collection containing BoE program documents keyed by programId
 * (e.g. "0200", "0300", ...).
 */
const BOARD_OF_ED_PROGRAMS_COLLECTION = "publicSchools";

/**
 * Sort role keys using POSITION_ORDER first, then alphabetical.
 *
 * NOTE:
 * We keep this local because staffSnapshotUtils exports POSITION_ORDER but not a sorter.
 *
 * @param {string[]} keys
 * @returns {string[]}
 */
function sortPositionKeys(keys) {
  const orderMap = new Map(POSITION_ORDER.map((k, i) => [k, i]));
  return [...keys].sort((a, b) => {
    const ia = orderMap.has(a) ? orderMap.get(a) : 9999;
    const ib = orderMap.has(b) ? orderMap.get(b) : 9999;
    if (ia !== ib) return ia - ib;
    return String(a).localeCompare(String(b));
  });
}

/**
 * Renders a role label with a popover listing contributing programs.
 *
 * (Kept local to avoid forcing React/UI imports into the utils module.)
 */
function RoleLabelWithProgramsPopover({
  label,
  breakdown,
  windowYearsMeta,
  programMetaById,
}) {
  const hasAny = breakdown && Object.keys(breakdown).length > 0;

  const lastMeta = windowYearsMeta?.[windowYearsMeta.length - 1];
  const lastColKey = lastMeta?.colKey;

  const programIdSet = useMemo(() => {
    const set = new Set();
    if (!breakdown) return set;

    Object.values(breakdown).forEach((arr) => {
      (arr || []).forEach((x) => {
        if (x?.programId) set.add(x.programId);
      });
    });

    return set;
  }, [breakdown]);

  const list = useMemo(() => {
    const ids = Array.from(programIdSet);

    const latestMap = new Map();
    (breakdown?.[lastColKey] || []).forEach((x) => {
      if (x?.programId) latestMap.set(x.programId, x.value);
    });

    const rows = ids.map((programId) => {
      const fullName = programMetaById?.[programId]?.fullName;
      const programLabel = fullName ? `${programId} - ${fullName}` : programId;
      const latestVal = latestMap.has(programId) ? latestMap.get(programId) : 0;
      return { programId, programLabel, value: latestVal };
    });

    rows.sort((a, b) => {
      if (b.value !== a.value) return b.value - a.value;
      return String(a.programId).localeCompare(String(b.programId));
    });

    return rows;
  }, [programIdSet, breakdown, lastColKey, programMetaById]);

  const content = (
    <div className="p-3 text-xs">
      <div className="font-semibold text-sm text-gray-900 mb-2">{label}</div>

      {!list.length ? (
        <div className="text-gray-500 italic">No programs found.</div>
      ) : (
        <ul className="space-y-0.5">
          {list.map((x) => (
            <li
              key={`${label}-${x.programId}`}
              className="flex justify-between gap-4"
            >
              <span className="text-gray-700">{x.programLabel}</span>
              <span className="text-gray-900 font-medium">
                {formatFte(x.value)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <div className="relative flex items-center min-h-[22px] pr-6">
      <span className="leading-tight">{label}</span>

      {hasAny && (
        <div className="absolute right-1 top-1/2 -translate-y-1/2">
          <HoverPopover
            content={content}
            popoverClassName="text-gray-800"
            maxWidth={520}
          >
            <InformationCircleIcon
              className="w-4 h-4 text-gray-500 hover:text-gray-800 cursor-pointer"
              aria-label={`Show programs for ${label}`}
            />
          </HoverPopover>
        </div>
      )}
    </div>
  );
}

/**
 * StaffSnapshotByYear
 *
 * District-wide staffing snapshot driven by Positions/FTE across all BoE programs.
 */
export function StaffSnapshotByYear({ setBoeData }) {
  const excelExportRef = useRef(null);

  const [programDocs, setProgramDocs] = useState({});
  const [yearNumbers, setYearNumbers] = useState([]);
  const [endingYear, setEndingYear] = useSharedFilter("StaffSnapshotByYear.jsx:StaffSnapshotByYear:endingYear", null);
  const [loading, setLoading] = useState(true);

  // Collapsed sections (match original UX)
  const [showApprovedStaffChart, setShowApprovedStaffChart] = useState(false);
  const [showActualStaffChart, setShowActualStaffChart] = useState(false);
  const [showDiffStaffChart, setShowDiffStaffChart] = useState(false);
  const [showSnapshotTable, setShowSnapshotTable] = useState(true);
  const [showSalaryIncreaseChart, setShowSalaryIncreaseChart] = useState(false);
  const [showSalaryDollarIncreaseChart, setShowSalaryDollarIncreaseChart] =
    useState(false);
  const [showAvgSalaryChart, setShowAvgSalaryChart] = useState(false);

  // ---------- Load all program docs once --------------------------------
  useEffect(() => {
    let alive = true;

    async function loadAllPrograms() {
      setLoading(true);
      try {
        const snaps = await Promise.all(
          BOE_PROGRAM_DOC_IDS.map((id) =>
            getDoc(doc(db, BOARD_OF_ED_PROGRAMS_COLLECTION, id)),
          ),
        );

        if (!alive) return;

        const docsById = {};
        const yearSet = new Set();

        snaps.forEach((snap, idx) => {
          const id = BOE_PROGRAM_DOC_IDS[idx];
          if (!snap.exists()) {
            console.warn(
              `Program doc ${BOARD_OF_ED_PROGRAMS_COLLECTION}/${id} not found`,
            );
            return;
          }
          const data = snap.data();
          docsById[id] = data;

          Object.keys(data || {}).forEach((key) => {
            const y = toYearNumber(key);
            if (y) yearSet.add(y);
          });
        });

        setProgramDocs(docsById);
        setYearNumbers(Array.from(yearSet).sort((a, b) => a - b));
      } catch (err) {
        console.error("Error loading program docs for staff snapshot:", err);
        if (alive) {
          setProgramDocs({});
          setYearNumbers([]);
          setEndingYear.initialize(null);
        }
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadAllPrograms();
    return () => {
      alive = false;
    };
  }, []);

  // ---------- Availability / Ending year options ------------------------
  const yearAvail = useMemo(
    () => buildYearAvailability(programDocs),
    [programDocs],
  );

  /**
   * Approved chart w/ proposed-only tail (years where Proposed exists but Approved does not).
   * Kept in-component because it’s a specific UX rule, but uses utility primitives.
   */
  function buildApprovedStaffChartWithProposedTail() {
    const approvedYears = getYearsForSection(yearAvail, "Approved");
    const proposedYears = getYearsForSection(yearAvail, "Proposed");

    const yearsSet = new Set(approvedYears);
    proposedYears.forEach((y) => {
      const slot = yearAvail.get(y);
      const hasApproved = slot?.hasApproved === true;
      const hasProposed = slot?.hasProposed === true;
      if (hasProposed && !hasApproved) yearsSet.add(y);
    });

    const years = Array.from(yearsSet).sort((a, b) => a - b);
    if (!years.length) return { labels: [], series: [] };

    // Role universe = roles across Approved + Proposed-only tail
    const roleKeySet = new Set();
    approvedYears.forEach((y) => {
      collectRoleKeysForYears(programDocs, [y], "Approved").forEach((k) =>
        roleKeySet.add(k),
      );
    });

    years.forEach((y) => {
      const slot = yearAvail.get(y);
      const hasApproved = slot?.hasApproved === true;
      const hasProposed = slot?.hasProposed === true;
      if (hasProposed && !hasApproved) {
        collectRoleKeysForYears(programDocs, [y], "Proposed").forEach((k) =>
          roleKeySet.add(k),
        );
      }
    });

    const roleKeys = sortPositionKeys(Array.from(roleKeySet));
    if (!roleKeys.length) return { labels: [], series: [] };

    const labels = years.map((y) => formatFiscalYearFromStart(y));

    const totalSeries = {
      key: "Total",
      label: "Total Staff (FTE)",
      data: years.map((y) => {
        const hasApproved = yearAvail.get(y)?.hasApproved === true;
        if (!hasApproved) return null;

        return roleKeys.reduce(
          (acc, roleKey) =>
            acc +
            sumRoleAcrossPrograms(programDocs, {
              year: y,
              sectionKey: "Approved",
              roleKey,
            }),
          0,
        );
      }),
      proposedData: years.map((y) => {
        const slot = yearAvail.get(y);
        const hasApproved = slot?.hasApproved === true;
        const hasProposed = slot?.hasProposed === true;
        if (!hasProposed || hasApproved) return null;

        return roleKeys.reduce(
          (acc, roleKey) =>
            acc +
            sumRoleAcrossPrograms(programDocs, {
              year: y,
              sectionKey: "Proposed",
              roleKey,
            }),
          0,
        );
      }),
      group: "staffTotals",
      valueType: "count",
      defaultVisible: false,
    };

    const roleSeries = roleKeys.map((roleKey) => {
      const key = slugifyKey(roleKey);

      return {
        key,
        label: roleKey,
        data: years.map((y) => {
          const hasApproved = yearAvail.get(y)?.hasApproved === true;
          if (!hasApproved) return null;
          return sumRoleAcrossPrograms(programDocs, {
            year: y,
            sectionKey: "Approved",
            roleKey,
          });
        }),
        proposedData: years.map((y) => {
          const slot = yearAvail.get(y);
          const hasApproved = slot?.hasApproved === true;
          const hasProposed = slot?.hasProposed === true;
          if (!hasProposed || hasApproved) return null;
          return sumRoleAcrossPrograms(programDocs, {
            year: y,
            sectionKey: "Proposed",
            roleKey,
          });
        }),
        group: getStaffPositionGroup(roleKey),
        valueType: "count",
        defaultVisible: false,
      };
    });

    return { labels, series: [totalSeries, ...roleSeries] };
  }

  /**
   * Avg salary % increase chart (Actual): YoY % change of (Salaries ÷ Positions).
   */
  function buildAvgSalaryIncreaseChart() {
    const sectionKey = "Actual";
    const years = getYearsForSection(yearAvail, sectionKey);
    if (years.length < 2) return { labels: [], series: [] };

    const roleKeys = collectRoleKeysForYears(programDocs, years, sectionKey);

    const filteredRoleKeys = roleKeys.filter((roleKey) =>
      years.some((y) => {
        const fte = sumRoleAcrossPrograms(programDocs, {
          year: y,
          sectionKey,
          roleKey,
        });
        if (!Number.isFinite(fte) || fte === 0) return false;

        const sal = sumSalaryAcrossPrograms(programDocs, {
          year: y,
          sectionKey,
          roleKey,
        });
        return Number.isFinite(sal) && sal !== 0;
      }),
    );

    if (!filteredRoleKeys.length) return { labels: [], series: [] };

    const avgTotalByYear = years.map((y) => {
      let totalSal = 0;
      let totalFte = 0;

      filteredRoleKeys.forEach((roleKey) => {
        const fte = sumRoleAcrossPrograms(programDocs, {
          year: y,
          sectionKey,
          roleKey,
        });
        const sal = sumSalaryAcrossPrograms(programDocs, {
          year: y,
          sectionKey,
          roleKey,
        });
        if (Number.isFinite(fte) && fte > 0 && Number.isFinite(sal)) {
          totalFte += fte;
          totalSal += sal;
        }
      });

      if (!Number.isFinite(totalFte) || totalFte === 0) return null;
      return totalSal / totalFte;
    });

    const pctTotalByYear = avgTotalByYear.map((v, idx) => {
      if (idx === 0) return null;
      const prev = avgTotalByYear[idx - 1];
      if (!Number.isFinite(v) || !Number.isFinite(prev) || prev === 0)
        return null;
      return ((v - prev) / prev) * 100;
    });

    const labels = years.slice(1).map((y) => formatFiscalYearFromStart(y));
    const series = [
      {
        key: "Total",
        label: "Total",
        data: pctTotalByYear.slice(1),
        group: "staffTotals",
        valueType: "percent",
        defaultVisible: true,
      },
    ];

    filteredRoleKeys.forEach((roleKey) => {
      const avgByYear = years.map((y) =>
        avgSalaryPerFteAcrossPrograms(programDocs, {
          year: y,
          sectionKey,
          roleKey,
        }),
      );
      const validCount = avgByYear.filter((v) => Number.isFinite(v)).length;
      if (validCount < 2) return;

      const pctByYear = avgByYear.map((v, idx) => {
        if (idx === 0) return null;
        const prev = avgByYear[idx - 1];
        if (!Number.isFinite(v) || !Number.isFinite(prev) || prev === 0)
          return null;
        return ((v - prev) / prev) * 100;
      });

      series.push({
        key: `avg_salary_increase_${slugifyKey(roleKey)}`,
        label: roleKey,
        data: pctByYear.slice(1),
        group: getStaffPositionGroup(roleKey),
        valueType: "percent",
        defaultVisible: false,
      });
    });

    return { labels, series };
  }

  /**
   * Avg salary $ increase chart (Actual): YoY $ change of (Salaries ÷ Positions).
   */
  function buildAvgSalaryDollarIncreaseChart() {
    const sectionKey = "Actual";
    const years = getYearsForSection(yearAvail, sectionKey);
    if (years.length < 2) return { labels: [], series: [] };

    const roleKeys = collectRoleKeysForYears(programDocs, years, sectionKey);

    const filteredRoleKeys = roleKeys.filter((roleKey) =>
      years.some((y) => {
        const fte = sumRoleAcrossPrograms(programDocs, {
          year: y,
          sectionKey,
          roleKey,
        });
        if (!Number.isFinite(fte) || fte === 0) return false;

        const sal = sumSalaryAcrossPrograms(programDocs, {
          year: y,
          sectionKey,
          roleKey,
        });
        return Number.isFinite(sal) && sal !== 0;
      }),
    );

    if (!filteredRoleKeys.length) return { labels: [], series: [] };

    const avgTotalByYear = years.map((y) => {
      let totalSal = 0;
      let totalFte = 0;

      filteredRoleKeys.forEach((roleKey) => {
        const fte = sumRoleAcrossPrograms(programDocs, {
          year: y,
          sectionKey,
          roleKey,
        });
        const sal = sumSalaryAcrossPrograms(programDocs, {
          year: y,
          sectionKey,
          roleKey,
        });
        if (Number.isFinite(fte) && fte > 0 && Number.isFinite(sal)) {
          totalFte += fte;
          totalSal += sal;
        }
      });

      if (!Number.isFinite(totalFte) || totalFte === 0) return null;
      return totalSal / totalFte;
    });

    const dollarTotalByYear = avgTotalByYear.map((v, idx) => {
      if (idx === 0) return null;
      const prev = avgTotalByYear[idx - 1];
      if (!Number.isFinite(v) || !Number.isFinite(prev)) return null;
      return v - prev;
    });

    const labels = years.slice(1).map((y) => formatFiscalYearFromStart(y));
    const series = [
      {
        key: "Total",
        label: "Total",
        data: dollarTotalByYear.slice(1),
        group: "staffTotals",
        valueType: "currency",
        defaultVisible: true,
      },
    ];

    filteredRoleKeys.forEach((roleKey) => {
      const avgByYear = years.map((y) =>
        avgSalaryPerFteAcrossPrograms(programDocs, {
          year: y,
          sectionKey,
          roleKey,
        }),
      );
      const validCount = avgByYear.filter((v) => Number.isFinite(v)).length;
      if (validCount < 2) return;

      const dollarByYear = avgByYear.map((v, idx) => {
        if (idx === 0) return null;
        const prev = avgByYear[idx - 1];
        if (!Number.isFinite(v) || !Number.isFinite(prev)) return null;
        return v - prev;
      });

      series.push({
        key: `avg_salary_dollar_increase_${slugifyKey(roleKey)}`,
        label: roleKey,
        data: dollarByYear.slice(1),
        group: getStaffPositionGroup(roleKey),
        valueType: "currency",
        defaultVisible: false,
      });
    });

    return { labels, series };
  }

  /**
   * Avg salary level chart (Actual): (Salaries ÷ Positions) per role, per year.
   * Tooltip uses per-point meta: totalSalary, totalFte, avgSalary.
   */
  function buildAvgSalaryLevelChart() {
    const sectionKey = "Actual";
    const years = getYearsForSection(yearAvail, sectionKey);
    if (!years.length) return { labels: [], series: [] };

    const roleKeys = collectRoleKeysForYears(programDocs, years, sectionKey);

    const filteredRoleKeys = roleKeys.filter((roleKey) =>
      years.some((y) => {
        const fte = sumRoleAcrossPrograms(programDocs, {
          year: y,
          sectionKey,
          roleKey,
        });
        const sal = sumSalaryAcrossPrograms(programDocs, {
          year: y,
          sectionKey,
          roleKey,
        });
        return (
          Number.isFinite(fte) && fte > 0 && Number.isFinite(sal) && sal !== 0
        );
      }),
    );

    if (!filteredRoleKeys.length) return { labels: [], series: [] };

    const labels = years.map((y) => formatFiscalYearFromStart(y));

    const totalAvgByYear = years.map((y) => {
      let totalSal = 0;
      let totalFte = 0;

      filteredRoleKeys.forEach((roleKey) => {
        const fte = sumRoleAcrossPrograms(programDocs, {
          year: y,
          sectionKey,
          roleKey,
        });
        const sal = sumSalaryAcrossPrograms(programDocs, {
          year: y,
          sectionKey,
          roleKey,
        });
        if (Number.isFinite(fte) && fte > 0 && Number.isFinite(sal)) {
          totalFte += fte;
          totalSal += sal;
        }
      });

      if (!Number.isFinite(totalFte) || totalFte === 0) return null;
      return totalSal / totalFte;
    });

    const totalMetaByYear = years.map((y, idx) => {
      let totalSalary = 0;
      let totalFte = 0;

      filteredRoleKeys.forEach((roleKey) => {
        const fte = sumRoleAcrossPrograms(programDocs, {
          year: y,
          sectionKey,
          roleKey,
        });
        const sal = sumSalaryAcrossPrograms(programDocs, {
          year: y,
          sectionKey,
          roleKey,
        });
        if (Number.isFinite(fte) && fte > 0 && Number.isFinite(sal)) {
          totalFte += fte;
          totalSalary += sal;
        }
      });

      const avgSalary = Number.isFinite(totalAvgByYear[idx])
        ? totalAvgByYear[idx]
        : null;

      return {
        totalSalary: Number.isFinite(totalSalary) ? totalSalary : null,
        totalFte: Number.isFinite(totalFte) ? totalFte : null,
        avgSalary,
      };
    });

    const series = [
      {
        key: "Total",
        label: "Total",
        data: totalAvgByYear,
        meta: totalMetaByYear,
        group: "staffTotals",
        valueType: "currency",
        defaultVisible: true,
      },
    ];

    filteredRoleKeys.forEach((roleKey) => {
      const avgByYear = years.map((y) =>
        avgSalaryPerFteAcrossPrograms(programDocs, {
          year: y,
          sectionKey,
          roleKey,
        }),
      );

      const metaByYear = years.map((y, idx) => {
        const totalFte = sumRoleAcrossPrograms(programDocs, {
          year: y,
          sectionKey,
          roleKey,
        });
        const totalSalary = sumSalaryAcrossPrograms(programDocs, {
          year: y,
          sectionKey,
          roleKey,
        });
        const avgSalary = Number.isFinite(avgByYear[idx])
          ? avgByYear[idx]
          : null;

        return {
          totalSalary: Number.isFinite(totalSalary) ? totalSalary : null,
          totalFte: Number.isFinite(totalFte) ? totalFte : null,
          avgSalary,
        };
      });

      const validCount = avgByYear.filter((v) => Number.isFinite(v)).length;
      if (validCount < 1) return;

      series.push({
        key: `avg_salary_level_${slugifyKey(roleKey)}`,
        label: roleKey,
        data: avgByYear,
        meta: metaByYear,
        group: getStaffPositionGroup(roleKey),
        valueType: "currency",
        defaultVisible: false,
      });
    });

    return { labels, series };
  }

  /**
   * Diff chart: Approved vs Actual, overlapping years.
   * We keep it local because it emits groups (toggle groupings).
   */
  function buildStaffDiffChart() {
    const approvedYears = getYearsForSection(yearAvail, "Approved");
    const actualYears = getYearsForSection(yearAvail, "Actual");

    const actualSet = new Set(actualYears);
    const years = approvedYears.filter((y) => actualSet.has(y));
    if (!years.length) return { labels: [], series: [], groups: [] };

    const roleKeySet = new Set();
    collectRoleKeysForYears(programDocs, years, "Approved").forEach((k) =>
      roleKeySet.add(k),
    );
    collectRoleKeysForYears(programDocs, years, "Actual").forEach((k) =>
      roleKeySet.add(k),
    );

    const roleKeys = sortPositionKeys(Array.from(roleKeySet));
    if (!roleKeys.length) return { labels: [], series: [], groups: [] };

    const labels = years.map((y) => formatFiscalYearFromStart(y));
    const series = [];
    const groups = [];

    const approvedTotalKey = "approved___total__";
    const actualTotalKey = "actual___total__";

    series.push(
      {
        key: approvedTotalKey,
        label: "Approved – Total Staff (FTE)",
        data: years.map((y) =>
          roleKeys.reduce(
            (acc, roleKey) =>
              acc +
              sumRoleAcrossPrograms(programDocs, {
                year: y,
                sectionKey: "Approved",
                roleKey,
              }),
            0,
          ),
        ),
        group: "staffTotals",
        valueType: "count",
        defaultVisible: true,
      },
      {
        key: actualTotalKey,
        label: "Actual – Total Staff (FTE)",
        data: years.map((y) =>
          roleKeys.reduce(
            (acc, roleKey) =>
              acc +
              sumRoleAcrossPrograms(programDocs, {
                year: y,
                sectionKey: "Actual",
                roleKey,
              }),
            0,
          ),
        ),
        group: "staffTotals",
        valueType: "count",
        defaultVisible: true,
      },
    );

    groups.push({
      groupKey: "staffTotals",
      key: "role___total__",
      label: "Total Staff (FTE)",
      seriesKeys: [approvedTotalKey, actualTotalKey],
    });

    roleKeys.forEach((roleKey) => {
      const slug = slugifyKey(roleKey);
      const aKey = `approved_${slug}`;
      const bKey = `actual_${slug}`;

      series.push(
        {
          key: aKey,
          label: `Approved – ${roleKey}`,
          data: years.map((y) =>
            sumRoleAcrossPrograms(programDocs, {
              year: y,
              sectionKey: "Approved",
              roleKey,
            }),
          ),
          group: getStaffPositionGroup(roleKey),
          valueType: "count",
          defaultVisible: false,
        },
        {
          key: bKey,
          label: `Actual – ${roleKey}`,
          data: years.map((y) =>
            sumRoleAcrossPrograms(programDocs, {
              year: y,
              sectionKey: "Actual",
              roleKey,
            }),
          ),
          group: getStaffPositionGroup(roleKey),
          valueType: "count",
          defaultVisible: false,
        },
      );

      groups.push({
        groupKey: getStaffPositionGroup(roleKey),
        key: `role_${slug}`,
        label: roleKey,
        seriesKeys: [aKey, bKey],
      });
    });

    return { labels, series, groups };
  }

  // ---------- Chart payloads (memoized) ----------------------------------
  const { labels: approvedStaffLabels, series: approvedStaffSeries } = useMemo(
    () => buildApprovedStaffChartWithProposedTail(),
    [programDocs, yearAvail],
  );

  const { labels: actualStaffLabels, series: actualStaffSeries } = useMemo(
    () =>
      buildStaffChartForSection({
        programDocs,
        yearAvail,
        sectionKey: "Actual",
      }),
    [programDocs, yearAvail],
  );

  const { labels: salaryIncLabels, series: salaryIncSeries } = useMemo(
    () => buildAvgSalaryIncreaseChart(),
    [programDocs, yearAvail],
  );

  const { labels: avgSalaryLabels, series: avgSalarySeries } = useMemo(
    () => buildAvgSalaryLevelChart(),
    [programDocs, yearAvail],
  );

  const {
    labels: diffStaffLabels,
    series: diffStaffSeries,
    groups: diffStaffGroups,
  } = useMemo(() => buildStaffDiffChart(), [programDocs, yearAvail]);

  const { labels: salaryDollarIncLabels, series: salaryDollarIncSeries } =
    useMemo(
      () => buildAvgSalaryDollarIncreaseChart(),
      [programDocs, yearAvail],
    );

  // ---------- Ending year options ----------------------------------------
  const endingYearOptions = useMemo(() => {
    if (!yearNumbers.length) return [];

    const hasActual = (y) => yearAvail.get(y)?.hasActual === true;
    const hasApproved = (y) => yearAvail.get(y)?.hasApproved === true;
    const hasProposed = (y) => yearAvail.get(y)?.hasProposed === true;

    const isValidNormal = (y) =>
      hasActual(y - 2) &&
      hasApproved(y - 1) &&
      hasActual(y - 1) &&
      hasApproved(y);

    const isValidProposedOnly = (y) =>
      hasProposed(y) &&
      !hasApproved(y) &&
      hasActual(y - 3) &&
      hasActual(y - 2) &&
      hasApproved(y - 1);

    return yearNumbers
      .filter((y) => isValidNormal(y) || isValidProposedOnly(y))
      .sort((a, b) => a - b);
  }, [yearNumbers, yearAvail]);

  useEffect(() => {
    if (!endingYearOptions.length) return;
    setEndingYear.initialize((prev) =>
      prev && endingYearOptions.includes(prev)
        ? prev
        : endingYearOptions[endingYearOptions.length - 1],
    );
  }, [endingYearOptions]);

  const isProposedMode = useMemo(() => {
    if (!endingYear) return false;
    const slot = yearAvail.get(endingYear);
    return slot?.hasProposed === true && slot?.hasApproved !== true;
  }, [endingYear, yearAvail]);

  // ---------- 4-column window meta --------------------------------------
  const windowYearsMeta = useMemo(() => {
    if (!endingYear) return [];

    if (isProposedMode) {
      return [
        {
          year: endingYear - 3,
          sectionKey: "Actual",
          colKey: `actual_${endingYear - 3}`,
        },
        {
          year: endingYear - 2,
          sectionKey: "Actual",
          colKey: `actual_${endingYear - 2}`,
        },
        {
          year: endingYear - 1,
          sectionKey: "Approved",
          colKey: `approved_${endingYear - 1}`,
        },
        {
          year: endingYear,
          sectionKey: "Proposed",
          colKey: `proposed_${endingYear}`,
        },
      ];
    }

    return [
      {
        year: endingYear - 2,
        sectionKey: "Actual",
        colKey: `actual_${endingYear - 2}`,
      },
      {
        year: endingYear - 1,
        sectionKey: "Approved",
        colKey: `approved_${endingYear - 1}`,
      },
      {
        year: endingYear - 1,
        sectionKey: "Actual",
        colKey: `actual_${endingYear - 1}`,
      },
      {
        year: endingYear,
        sectionKey: "Approved",
        colKey: `approved_${endingYear}`,
      },
    ];
  }, [endingYear, isProposedMode]);

  const programMetaById = useMemo(() => {
    const out = {};
    Object.entries(programDocs || {}).forEach(([programId, docData]) => {
      out[programId] = { fullName: docData?.fullName || null };
    });
    return out;
  }, [programDocs]);

  // ---------- Table columns ---------------------------------------------
  const tableColumns = useMemo(() => {
    if (!windowYearsMeta.length) return [];

    const LABEL_COL_WIDTH = "320px";
    const YEAR_COL_WIDTH = "140px";
    const DELTA_COL_WIDTH = "100px";

    const cols = [
      {
        key: "label",
        label: "",
        style: { width: LABEL_COL_WIDTH, minWidth: LABEL_COL_WIDTH },
        editable: false,
        render: (row) => (
          <RoleLabelWithProgramsPopover
            label={row.label}
            breakdown={row._breakdown}
            windowYearsMeta={windowYearsMeta}
            programMetaById={programMetaById}
          />
        ),
      },
    ];

    const proposedColKey = isProposedMode
      ? windowYearsMeta.find((m) => m.sectionKey === "Proposed")?.colKey
      : null;

    windowYearsMeta.forEach(({ year, sectionKey, colKey }) => {
      const fy = formatFiscalYearFromStart(year);
      const sectionLabel =
        sectionKey === "Approved"
          ? "Approved"
          : sectionKey === "Proposed"
            ? "Proposed"
            : "Actual";

      const isHighlighted = proposedColKey && colKey === proposedColKey;

      cols.push({
        key: colKey,
        label: buildTwoLineHeader(fy, sectionLabel),
        align: "right",
        style: { width: YEAR_COL_WIDTH, minWidth: YEAR_COL_WIDTH },
        headerClassName: isHighlighted ? "bg-yellow-100" : undefined,
        cellClassName: isHighlighted ? "bg-yellow-50" : undefined,
      });
    });

    cols.push(
      {
        key: "increase",
        label: "Change",
        align: "right",
        style: { width: DELTA_COL_WIDTH, minWidth: DELTA_COL_WIDTH },
      },
      {
        key: "percentIncrease",
        label: "% Change",
        format: "percent",
        align: "right",
        style: { width: DELTA_COL_WIDTH, minWidth: DELTA_COL_WIDTH },
      },
    );

    return cols;
  }, [windowYearsMeta, isProposedMode, programMetaById]);

  // ---------- Rows -------------------------------------------------------
  const staffRows = useMemo(() => {
    if (!Object.keys(programDocs).length || !windowYearsMeta.length) return [];

    const keySet = new Set();
    Object.values(programDocs).forEach((docData) => {
      windowYearsMeta.forEach(({ year, sectionKey }) => {
        const positions = docData?.[String(year)]?.[sectionKey]?.Positions;
        if (!positions || typeof positions !== "object") return;
        Object.keys(positions).forEach((k) => keySet.add(k));
      });
    });

    const positionKeys = sortPositionKeys(Array.from(keySet));
    const rows = [];

    positionKeys.forEach((posKey) => {
      const row = { label: posKey };
      let hasAny = false;

      const breakdown = {}; // colKey -> [{ programId, value }]

      windowYearsMeta.forEach(({ year, sectionKey, colKey }) => {
        let sum = 0;
        const contributors = [];

        Object.entries(programDocs).forEach(([programId, docData]) => {
          const v = docData?.[String(year)]?.[sectionKey]?.Positions?.[posKey];
          if (typeof v === "number" && Number.isFinite(v) && v !== 0) {
            sum += v;
            if (v > 0) contributors.push({ programId, value: v });
          }
        });

        if (contributors.length > 0 || sum !== 0) {
          row[colKey] = sum;

          if (contributors.length > 0) {
            breakdown[colKey] = contributors.sort((a, b) => b.value - a.value);
          }
          if (sum !== 0) hasAny = true;
        }
      });

      if (!hasAny) return;

      row._breakdown = breakdown;
      attachIncreaseFields(row, windowYearsMeta);
      rows.push(row);
    });

    return rows;
  }, [programDocs, windowYearsMeta]);

  const totalStaffRow = useMemo(() => {
    if (!staffRows.length || !windowYearsMeta.length) return null;

    const row = { label: "Total Positions (FTE)", bold: true };

    windowYearsMeta.forEach(({ colKey }) => {
      const sum = staffRows.reduce((acc, r) => {
        const v = r[colKey];
        return typeof v === "number" ? acc + v : acc;
      }, 0);
      row[colKey] = sum;
    });

    attachIncreaseFields(row, windowYearsMeta);
    return row;
  }, [staffRows, windowYearsMeta]);

  // Optional bubble-up
  useEffect(() => {
    if (typeof setBoeData !== "function") return;
    if (!staffRows.length) return;

    setBoeData((prev) => ({
      ...prev,
      staffSnapshotByYear: {
        description:
          "Staff (Positions/FTE) snapshot by year, summed across all programs",
        rows: staffRows,
      },
    }));
  }, [staffRows, setBoeData]);

  // ---------- Render ----------------------------------------------------
  if (loading) {
    return (
      <div className="p-4 text-sm text-gray-500">Loading staff snapshot…</div>
    );
  }

  const has4ColWindow =
    endingYear &&
    windowYearsMeta.length === 4 &&
    tableColumns.length > 0 &&
    staffRows.length > 0;

  if (!has4ColWindow) {
    return (
      <div className="p-4 text-sm text-gray-500">
        Staff (Positions) data is not available for the selected window.
      </div>
    );
  }

  const displayHeading = "Staff";

  return (
    <TableSearchScope>
    <div className="space-y-8">
      {/* TABLE (snapshot window) */}
      <div className="space-y-3">
        <HeaderToggleButton
          isOpen={showSnapshotTable}
          onToggle={() => setShowSnapshotTable((prev) => !prev)}
          title="Staff Snapshot by Year"
          ariaLabel="Toggle staff snapshot table"
          controlsId="staff-snapshot-table"
          rightSlot={
            endingYearOptions.length > 0 ? (
              <div
                className="ml-auto flex flex-wrap items-center justify-end gap-2 pr-2 pointer-events-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Excel export button (unstyled icon) */}
                <button
                  type="button"
                  className="inline-flex items-center justify-center bg-transparent border-0 p-0 m-0 text-gray-700 hover:text-gray-900 focus:outline-none leading-none"
                  style={{ lineHeight: 1 }}
                  title="Download table to Excel"
                  aria-label="Download table to Excel"
                  onClick={(e) => {
                    e.stopPropagation();
                    excelExportRef.current?.exportToExcel?.();
                  }}
                >
                  <span className="relative top-[1px] text-base">💾</span>
                </button>

                {showSnapshotTable && <SectionTableSearch />}
                <span className="shrink-0 whitespace-nowrap font-semibold text-xs text-gray-700">
                  Ending Year
                </span>

                <select
                  className="border rounded px-2 py-1 text-xs bg-white"
                  value={endingYear || ""}
                  onChange={(e) =>
                    setEndingYear(
                      e.target.value ? Number(e.target.value) : endingYear,
                    )
                  }
                >
                  {endingYearOptions.map((y) => (
                    <option key={y} value={y}>
                      {formatFiscalYearFromStart(y)}
                    </option>
                  ))}
                </select>
              </div>
            ) : null
          }
        />

        {showSnapshotTable && (
          <div id="staff-snapshot-table">
            <CommonTable
              excelExportRef={excelExportRef}
              excelFilename={`staff-snapshot-by-year-${endingYear || "latest"}.xlsx`}
              excelSheetName="Staff Snapshot"
              excelTitle="Staff Snapshot by Year"
              title={null}
              columns={tableColumns}
              data={totalStaffRow ? [...staffRows, totalStaffRow] : staffRows}
              isEditing={false}
              headerColor="bg-[#e5eef9]"
              showAggregateTotal={false}
              defaultSortField={null}
              defaultSortOrder="asc"
            />
          </div>
        )}
      </div>

      {/* Staff – Approved */}
      {approvedStaffLabels.length > 0 && approvedStaffSeries.length > 0 && (
        <div className="space-y-1">
          <HeaderToggleButton
            isOpen={showApprovedStaffChart}
            onToggle={() => setShowApprovedStaffChart((prev) => !prev)}
            title={`${displayHeading} – Approved`}
            ariaLabel="Toggle approved staff chart"
            controlsId="staff-approved-chart"
          />

          {showApprovedStaffChart && (
            <div id="staff-approved-chart">
              <p className="text-xs text-gray-600 mb-1">
                Approved staffing (FTE) totals across all programs, with
                optional role breakdown.
              </p>

              <LineChartWithToggles
                labels={approvedStaffLabels}
                series={approvedStaffSeries}
                showTotalWhenNoneSelected={true}
                showSeriesToggles={true}
                totalLabel="Total Staff (FTE)"
                proposedRenderMode="merge"
              />
            </div>
          )}
        </div>
      )}

      {/* Staff – Actual */}
      {actualStaffLabels.length > 0 && actualStaffSeries.length > 0 && (
        <div className="space-y-1">
          <HeaderToggleButton
            isOpen={showActualStaffChart}
            onToggle={() => setShowActualStaffChart((prev) => !prev)}
            title={`${displayHeading} – Actual`}
            ariaLabel="Toggle actual staff chart"
            controlsId="staff-actual-chart"
          />

          {showActualStaffChart && (
            <div id="staff-actual-chart">
              <p className="text-xs text-gray-600 mb-1">
                Actual staffing (FTE) totals across all programs, with optional
                role breakdown.
              </p>

              <LineChartWithToggles
                labels={actualStaffLabels}
                series={actualStaffSeries}
                showTotalWhenNoneSelected={true}
                showSeriesToggles={true}
                totalLabel="Total Staff (FTE)"
              />
            </div>
          )}
        </div>
      )}

      {/* Staff – Actual Spending vs Approved Budget */}
      {diffStaffLabels.length > 0 && diffStaffSeries.length > 0 && (
        <div className="space-y-1">
          <HeaderToggleButton
            isOpen={showDiffStaffChart}
            onToggle={() => setShowDiffStaffChart((prev) => !prev)}
            title={`${displayHeading} – Actual Spending vs Approved Budget`}
            ariaLabel="Toggle actual vs approved staff chart"
            controlsId="staff-diff-chart"
          />

          {showDiffStaffChart && (
            <div id="staff-diff-chart">
              <p className="text-xs text-gray-600 mb-1">
                Compare Approved staffing vs Actual staffing (FTE). Selecting a
                role shows both lines.
              </p>

              <LineChartWithToggles
                labels={diffStaffLabels}
                series={diffStaffSeries}
                groups={diffStaffGroups}
              />
            </div>
          )}
        </div>
      )}

      {/* Salary – Avg % Increase (Actual) */}
      {salaryIncLabels.length > 0 && salaryIncSeries.length > 0 && (
        <div className="space-y-1">
          <HeaderToggleButton
            isOpen={showSalaryIncreaseChart}
            onToggle={() => setShowSalaryIncreaseChart((prev) => !prev)}
            title="Salary – Average % Increase (Actual)"
            ariaLabel="Toggle average salary increase chart"
            controlsId="salary-increase-chart"
          />

          {showSalaryIncreaseChart && (
            <div id="salary-increase-chart">
              <p className="text-xs text-gray-600 mb-1">
                Year-over-year % change in average salary per FTE. Calculated as
                (total Salaries ÷ total Positions) by year, restricted to roles
                that exist in both Positions and Salaries. First year is blank
                by design.
              </p>

              <LineChartWithToggles
                labels={salaryIncLabels}
                series={salaryIncSeries}
                showTotalWhenNoneSelected={true}
                showSeriesToggles={true}
                totalLabel="Avg Salary % Increase (YoY) – Total"
              />
            </div>
          )}
        </div>
      )}

      {/* Salary – Avg $ Increase (Actual) */}
      {salaryDollarIncLabels.length > 0 && salaryDollarIncSeries.length > 0 && (
        <div className="space-y-1">
          <HeaderToggleButton
            isOpen={showSalaryDollarIncreaseChart}
            onToggle={() => setShowSalaryDollarIncreaseChart((prev) => !prev)}
            title="Salary – Average $ Increase (Actual)"
            ariaLabel="Toggle average salary dollar increase chart"
            controlsId="salary-dollar-increase-chart"
          />

          {showSalaryDollarIncreaseChart && (
            <div id="salary-dollar-increase-chart">
              <p className="text-xs text-gray-600 mb-1">
                Year-over-year $ change in average salary per FTE. Calculated as
                (total Salaries ÷ total Positions) by year, restricted to roles
                that exist in both Positions and Salaries. Baseline year is
                omitted because YoY change requires a prior year.
              </p>

              <LineChartWithToggles
                labels={salaryDollarIncLabels}
                series={salaryDollarIncSeries}
                showTotalWhenNoneSelected={true}
                showSeriesToggles={true}
                totalLabel="Avg Salary $ Increase (YoY) – Total"
              />
            </div>
          )}
        </div>
      )}

      {/* Salary – Average Salary (Actual) */}
      {avgSalaryLabels.length > 0 && avgSalarySeries.length > 0 && (
        <div className="space-y-1">
          <HeaderToggleButton
            isOpen={showAvgSalaryChart}
            onToggle={() => setShowAvgSalaryChart((prev) => !prev)}
            title="Salary – Average Salary (Actual)"
            ariaLabel="Toggle average salary chart"
            controlsId="avg-salary-chart"
          />

          {showAvgSalaryChart && (
            <div id="avg-salary-chart">
              <p className="text-xs text-gray-600 mb-1">
                Average salary per FTE by role (Actual). Hover shows total
                salary, total positions (FTE), and the computed average.
              </p>

              <LineChartWithToggles
                labels={avgSalaryLabels}
                series={avgSalarySeries}
                showTotalWhenNoneSelected={true}
                showSeriesToggles={true}
                totalLabel="Average Salary – Total"
              />
            </div>
          )}
        </div>
      )}
    </div>
    </TableSearchScope>
  );
}
